import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDish, useDishes } from '../hooks/useDishes';
import type { DishInput, MealSlot, Season } from '../types';
import { MEAL_SLOTS } from '../types';
import {
  formFieldStyle as fieldStyle,
  formNumberFieldStyle as numberFieldStyle,
  formNumberRowStyle as numberRowStyle,
  formSectionStyle as sectionStyle,
  formSectionTitleStyle as sectionTitleStyle,
  formSubmitStyle,
  fieldLabelStyle,
  pillToggleStyle,
} from '../lib/theme';

const pillRowStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' };

import ProductPicker from '../components/ProductPicker';

const SLOT_LABELS: Record<MealSlot, string> = {
  BREAKFAST: 'Breakfast',
  LUNCH: 'Lunch',
  DINNER: 'Dinner',
  SNACK: 'Snack',
};

const SEASONS: { value: Season; label: string }[] = [
  { value: 'ANY', label: 'Any' },
  { value: 'SPRING', label: 'Spring' },
  { value: 'SUMMER', label: 'Summer' },
  { value: 'FALL', label: 'Fall' },
  { value: 'WINTER', label: 'Winter' },
];

const EMPTY: DishInput = {
  name: '',
  photoUrl: '',
  prepTimeMinutes: undefined,
  costLevel: 2,
  portionDesc: '',
  calories: 0,
  proteinGrams: 0,
  satietyRating: 3,
  difficultyRating: 2,
  isFavorite: false,
  season: 'ANY',
  isFreezable: false,
  isDeliveryFriendly: false,
  mealSlots: [],
  products: [],
};

export default function DishForm() {
  const { id } = useParams();
  const dishId = id ? Number(id) : undefined;
  const { dish } = useDish(dishId);
  const { createDish, updateDish } = useDishes();
  const navigate = useNavigate();

  const [form, setForm] = useState<DishInput>(EMPTY);

  useEffect(() => {
    if (dish) {
      setForm({
        ...dish,
        products: dish.products.map((p) => ({ productId: p.id, amountGrams: p.amountGrams, calories: p.calories })),
      });
    }
  }, [dish]);

  function toggleSlot(slot: MealSlot) {
    setForm((f) => ({
      ...f,
      mealSlots: f.mealSlots.includes(slot) ? f.mealSlots.filter((s) => s !== slot) : [...f.mealSlots, slot],
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (dishId) await updateDish(dishId, form);
    else await createDish(form);
    navigate('/dishes');
  }

  return (
    <div>
      <h2>{dishId ? 'Edit dish' : 'Add dish'}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 520 }}>
        <section style={sectionStyle}>
          <label style={fieldLabelStyle}>
            Name
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              style={fieldStyle}
            />
          </label>

          <label style={fieldLabelStyle}>
            Photo URL
            <input
              type="url"
              value={form.photoUrl ?? ''}
              onChange={(e) => setForm({ ...form, photoUrl: e.target.value })}
              placeholder="https://…"
              style={fieldStyle}
            />
          </label>
          {form.photoUrl && (
            <img
              src={form.photoUrl}
              alt=""
              style={{ width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 14 }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
              onLoad={(e) => {
                e.currentTarget.style.display = 'block';
              }}
            />
          )}

          <label style={fieldLabelStyle}>
            Portion description
            <input
              value={form.portionDesc ?? ''}
              onChange={(e) => setForm({ ...form, portionDesc: e.target.value })}
              placeholder="e.g. 1 bowl"
              style={fieldStyle}
            />
          </label>
        </section>

        <section style={sectionStyle}>
          <div style={sectionTitleStyle}>Nutrition & prep</div>
          <div style={numberRowStyle}>
            <label style={numberFieldStyle}>
              Calories
              <input
                type="number"
                value={form.calories === 0 ? '' : form.calories}
                onChange={(e) => setForm({ ...form, calories: e.target.value === '' ? 0 : Number(e.target.value) })}
                required
                style={fieldStyle}
              />
            </label>
            <label style={numberFieldStyle}>
              Protein (g)
              <input
                type="number"
                value={form.proteinGrams === 0 ? '' : form.proteinGrams}
                onChange={(e) => setForm({ ...form, proteinGrams: e.target.value === '' ? 0 : Number(e.target.value) })}
                required
                style={fieldStyle}
              />
            </label>
            <label style={numberFieldStyle}>
              Prep (min)
              <input
                type="number"
                value={form.prepTimeMinutes ?? ''}
                onChange={(e) => setForm({ ...form, prepTimeMinutes: e.target.value ? Number(e.target.value) : null })}
                style={fieldStyle}
              />
            </label>
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionTitleStyle}>Meal slots</div>
          <div style={pillRowStyle}>
            {MEAL_SLOTS.map((slot) => {
              const active = form.mealSlots.includes(slot);
              return (
                <button
                  key={slot}
                  type="button"
                  aria-pressed={active}
                  onClick={() => toggleSlot(slot)}
                  style={pillToggleStyle(active)}
                >
                  {SLOT_LABELS[slot]}
                </button>
              );
            })}
          </div>
        </section>

        <section style={sectionStyle}>
          <div style={sectionTitleStyle}>Products</div>
          <ProductPicker selected={form.products} onChange={(products) => setForm({ ...form, products })} />
        </section>

        <section style={sectionStyle}>
          <div style={sectionTitleStyle}>Season</div>
          <div style={pillRowStyle}>
            {SEASONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                aria-pressed={form.season === value}
                onClick={() => setForm({ ...form, season: value })}
                style={pillToggleStyle(form.season === value)}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={sectionTitleStyle}>Tags</div>
          <div style={pillRowStyle}>
            <button
              type="button"
              aria-pressed={form.isFavorite}
              onClick={() => setForm({ ...form, isFavorite: !form.isFavorite })}
              style={pillToggleStyle(!!form.isFavorite)}
            >
              ★ Favorite
            </button>
            <button
              type="button"
              aria-pressed={form.isFreezable}
              onClick={() => setForm({ ...form, isFreezable: !form.isFreezable })}
              style={pillToggleStyle(!!form.isFreezable)}
            >
              Freezable
            </button>
            <button
              type="button"
              aria-pressed={form.isDeliveryFriendly}
              onClick={() => setForm({ ...form, isDeliveryFriendly: !form.isDeliveryFriendly })}
              style={pillToggleStyle(!!form.isDeliveryFriendly)}
            >
              Delivery-friendly
            </button>
          </div>
        </section>

        <button type="submit" style={formSubmitStyle}>
          {dishId ? 'Save changes' : 'Add dish'}
        </button>
      </form>
    </div>
  );
}
